import React, { useEffect, useState } from 'react';
import { X, Check, AlertCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { apiClient } from '../../lib/api/client';

// Status categories and statuses
const STATUS_CATEGORIES = {
  'Initial Processing': [
    'Uploaded',
    'Submissions',
    'Duplicates',
    'Remaining CVS',
    'CV Feedback pending',
    'CV Rejects',
  ],
  'Requirement Management': [
    'Req Closed',
    'REQ HOLD/CANCELLED',
    'REJECT - JD CHANGE',
  ],
  'Assessment & Testing': [
    'TEST LINK SHARED',
    'TEST COMPLETED/FB PENDING',
    'TEST SELECT,YET TO SCHEDULE L1',
    'TEST REJECT',
  ],
  'L1 Interviews': [
    'INTERVIEW-YTS( L-1)',
    'LEVEL 1 - SCHEDULED',
    'L1 Selects',
    'L1 Rejects',
  ],
  'L2 Interviews': [
    'L2 Selects',
    'L2 Rejects',
  ],
  'Offer & Onboarding': [
    'Offered',
    'Offer Drop',
    'Joined',
  ],
};

interface StatusUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  candidateId: string;
  requirementId: string;
  currentStatus?: string;
  initialStatus?: string;
  onStatusUpdated?: (newStatus: string) => void;
}

export const StatusUpdateModal: React.FC<StatusUpdateModalProps> = ({
  isOpen,
  onClose,
  candidateId,
  requirementId,
  currentStatus,
  initialStatus,
  onStatusUpdated,
}) => {
  const [selectedStatus, setSelectedStatus] = useState<string>(initialStatus || currentStatus || 'Uploaded');
  const [notes, setNotes] = useState<string>('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  useEffect(() => {
    if (isOpen) {
      setSelectedStatus(initialStatus || currentStatus || 'Uploaded');
      setNotes('');
      setError(null);
    }
  }, [isOpen, currentStatus, initialStatus]);

  const handleUpdate = async () => {
    if (!selectedStatus) {
      setError('Please select a status');
      return;
    }

    setIsUpdating(true);
    setError(null);

    try {
      await apiClient.updateCandidateStatus(candidateId, {
        status: selectedStatus,
        requirement_id: requirementId,
        notes: notes.trim() || undefined,
      });

      onClose();
      onStatusUpdated?.(selectedStatus);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to update status');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancel = () => {
    setNotes('');
    setError(null);
    setSelectedStatus(initialStatus || currentStatus || 'Uploaded');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-glass-surface dark:bg-glass-surface-dark-mode rounded-lg shadow-xl border border-glass-border dark:border-glass-border-dark-mode w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-glass-border dark:border-glass-border-dark-mode">
          <h2 className="text-xl font-semibold text-text-enterprise-primary">Update Candidate Status</h2>
          <button
            onClick={handleCancel}
            className="p-2 hover:bg-glass-surface/50 dark:hover:bg-glass-surface-dark-mode/50 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-text-enterprise-secondary" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Current Status */}
          {currentStatus && (
            <div>
              <label className="text-sm font-medium text-text-enterprise-secondary mb-2 block">
                Current Status
              </label>
              <div className="px-3 py-2 bg-glass-surface/50 dark:bg-glass-surface-dark-mode/50 rounded-lg border border-glass-border dark:border-glass-border-dark-mode">
                <span className="text-text-enterprise-primary">{currentStatus}</span>
              </div>
            </div>
          )}

          {/* Status Selection */}
          <div>
            <label className="text-sm font-medium text-text-enterprise-secondary mb-3 block">
              Select New Status <span className="text-red-500">*</span>
            </label>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {Object.entries(STATUS_CATEGORIES).map(([category, statuses]) => (
                <div key={category} className="space-y-2">
                  <h3 className="text-sm font-semibold text-text-enterprise-primary sticky top-0 bg-glass-surface dark:bg-glass-surface-dark-mode py-1">
                    {category}
                  </h3>
                  <div className="space-y-1 pl-4">
                    {statuses.map((status) => (
                      <label
                        key={status}
                        className="flex items-center space-x-2 p-2 rounded-lg hover:bg-glass-surface/50 dark:hover:bg-glass-surface-dark-mode/50 cursor-pointer"
                      >
                        <input
                          type="radio"
                          name="status"
                          value={status}
                          checked={selectedStatus === status}
                          onChange={(e) => setSelectedStatus(e.target.value)}
                          className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-text-enterprise-primary">{status}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-sm font-medium text-text-enterprise-secondary mb-2 block">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this status update..."
              className="w-full px-3 py-2 bg-glass-surface/50 dark:bg-glass-surface-dark-mode/50 rounded-lg border border-glass-border dark:border-glass-border-dark-mode text-text-enterprise-primary placeholder:text-text-tertiary resize-none focus:outline-none focus:ring-2 focus:ring-primary-500/50"
              rows={4}
              maxLength={1000}
            />
            <div className="text-xs text-text-tertiary mt-1 text-right">
              {notes.length}/1000 characters
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center space-x-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <AlertCircle className="w-4 h-4 text-red-500" />
              <span className="text-sm text-red-500">{error}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-glass-border dark:border-glass-border-dark-mode">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isUpdating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            disabled={isUpdating || !selectedStatus}
            className="flex items-center space-x-2"
          >
            {isUpdating ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Updating...</span>
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                <span>Update Status</span>
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

